"use client";

import React, { useMemo, useState, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { ListingTypeEnum } from "@/types/types";
import { toast } from "react-toastify";
import styles from "./media.module.css";
import { getMedia, MediaItem } from "@/lib/media/shared";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import SortablePhotoItem from "./sortablePhotoItem";
import { DeleteConfirmationModal } from "./deleteModal";
import MediaStateOverlay from "@/components/media-wrapper/media-state-overlay";
import { MdDelete, MdOutlineCategory } from "react-icons/md";
import { PhotoViewerDialog } from "./photoViewerDialog";
import { useListingContext } from "../listingContex";
import { uploadListingMedia } from "../media-upload";
import { CategoryOrganizerModal } from "./categoryOrganizerModal";
import { useScrollLock } from "@/hooks/use-scroll-lock";

// REMOVED: useBatchMediaPolling and browser-image-compression

export type LocalPreviewMediaItem = MediaItem & {
  localUrl?: string;
  file?: File;
  uniqueKey?: string;
  assignedKey?: string;
  room_tag?: string;
};

export default function PhotosUploader() {
  const { data, updateField } = useListingContext();
  const { userData } = useUserAuthDataContext();
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [pendingUploads, setPendingUploads] = useState<LocalPreviewMediaItem[]>(
    [],
  );

  useScrollLock(
    !!photoToDelete ||
      viewerIndex !== null ||
      showDeleteAllModal ||
      showCategoryModal,
  );

  const latestPhotosRef = useRef(data?.photos);
  useEffect(() => {
    latestPhotosRef.current = data?.photos;
  }, [data?.photos]);

  const confirmDelete = () => {
    if (photoToDelete) {
      const newArray = photoArray.filter((p) => p.MediaId !== photoToDelete);
      const newRecord: Record<string, MediaItem> = {};
      newArray.forEach((item, idx) => {
        newRecord[idx.toString()] = item;
      });
      updateField("photos", newRecord);

      setPendingUploads((prev) =>
        prev.filter((p) => p.uniqueKey !== photoToDelete),
      );
      setPhotoToDelete(null);
    }
  };

  const maxPhotos = useMemo(() => {
    if (data?.listing_type === ListingTypeEnum.PLUS) return 40;
    if (data?.listing_type === ListingTypeEnum.PRIME) return 60;
    if (data?.listing_type === ListingTypeEnum.SPOTLIGHT) return 80;
    return 20;
  }, [data?.listing_type]);

  const photoArray = useMemo(() => {
    if (!data?.photos) return [];
    return Object.entries(data.photos)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([_, item]) => item as LocalPreviewMediaItem);
  }, [data?.photos]);

  const onDrop = async (acceptedFiles: File[]) => {
    if (!data) return;
    if (
      photoArray.length + pendingUploads.length + acceptedFiles.length >
      maxPhotos
    ) {
      toast.error(`You can only upload up to ${maxPhotos} photos.`);
      return;
    }

    const currentMaxKey = Math.max(
      -1,
      ...Object.keys(data.photos || {}).map(Number),
    );
    let nextKey = currentMaxKey + 1 + pendingUploads.length;

    // 1. Instantly show placeholders
    const newPending = acceptedFiles.map((file) => ({
      uniqueKey: `local_${Date.now()}_${Math.random()}`,
      assignedKey: (nextKey++).toString(),
      MediaId: "",
      Status: "UPLOADED" as const,
      OriginalFileName: file.name,
      MediaCategory: "image" as const,
      FileExtension: file.name.split(".").pop() || "jpg",
      localUrl: URL.createObjectURL(file),
      file: file,
    }));

    setPendingUploads((prev) => [...prev, ...newPending]);

    // 2. BATCH QUEUE LOGIC (No compression)
    const CONCURRENCY_LIMIT = 3;
    const uploadQueue = [...newPending];

    const processNext = async () => {
      if (uploadQueue.length === 0) return;

      const pendingItem = uploadQueue.shift();
      if (!pendingItem) return;

      try {
        // Upload Pristine Original
        const completedMediaItem = await uploadListingMedia(
          pendingItem.file!,
          "image",
          data.listing_id,
          data.watermark_url || "",
        );

        // 1. Remove from local pending
        setPendingUploads((prev) =>
          prev.filter((p) => p.uniqueKey !== pendingItem.uniqueKey),
        );

        // 2. Add directly to Global DB Store!
        const newRecord = { ...(latestPhotosRef.current || {}) };
        newRecord[pendingItem.assignedKey!] = {
          ...completedMediaItem,
          localUrl: pendingItem.localUrl, // Keep local url for instant smooth viewing
        };
        updateField("photos", newRecord);
      } catch (err) {
        toast.error(`Failed to upload ${pendingItem.OriginalFileName}`);
        setPendingUploads((prev) =>
          prev.filter((p) => p.uniqueKey !== pendingItem.uniqueKey),
        );
      } finally {
        processNext();
      }
    };

    for (let i = 0; i < Math.min(CONCURRENCY_LIMIT, newPending.length); i++) {
      processNext();
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    maxSize: 10 * 1024 * 1024,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = photoArray.findIndex((p) => p.MediaId === active.id);
      const newIndex = photoArray.findIndex((p) => p.MediaId === over.id);
      const newArray = arrayMove(photoArray, oldIndex, newIndex);
      const newRecord: Record<string, MediaItem> = {};
      newArray.forEach((item, idx) => {
        newRecord[idx.toString()] = item;
      });
      updateField("photos", newRecord);
    }
  };

  const confirmDeleteAll = () => {
    updateField("photos", {});
    setPendingUploads([]);
    setShowDeleteAllModal(false);
    toast.info("All photos removed.");
  };

  const allViewablePhotos = useMemo(() => {
    const readyPhotos = photoArray.map((p) => {
      const resolved = getMedia(p, "lg");
      return {
        url: resolved?.src || p.localUrl || "",
        title: p.OriginalFileName || "Photo",
      };
    });

    const pendingPhotos = pendingUploads.map((p) => ({
      url: p.localUrl || "",
      title: p.OriginalFileName || "Uploading...",
    }));

    return [...readyPhotos, ...pendingPhotos].filter((p) => p.url !== "");
  }, [photoArray, pendingUploads]);

  return (
    <div className={styles.photosWrapper}>
      <div className={styles.subHeader}>
        <div className={styles.subHeaderWrapper}>
          <div>
            <h3 className={styles.subTitle}>Photos</h3>
            <p className={styles.subDescription}>
              {photoArray.length} / {maxPhotos} photos uploaded.
            </p>
          </div>

          {/* ACTIONS ROW */}
          {(photoArray.length > 0 || pendingUploads.length > 0) && (
            <div className={styles.headerButtons}>
              {photoArray.length > 0 && (
                <button
                  type="button"
                  className={styles.categorizeBtn} // We will add this CSS
                  onClick={() => setShowCategoryModal(true)}
                >
                  <MdOutlineCategory size={18} />
                  <span>Categorize</span>
                </button>
              )}
              <button
                type="button"
                className={styles.deleteAllBtn}
                onClick={() => setShowDeleteAllModal(true)}
              >
                <MdDelete size={18} />
                <span>Clear All</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.photoGrid}>
          <div {...getRootProps()} className={styles.dropzoneBox}>
            <input {...getInputProps()} />
            <div className={styles.dropzoneContent}>
              <span className={styles.dropzonePlus}>+</span>
              <p>Add Photos</p>
            </div>
          </div>

          <SortableContext
            items={photoArray.map((p) => p.MediaId)}
            strategy={rectSortingStrategy}
          >
            {photoArray.map((photo, index) => (
              <SortablePhotoItem
                key={photo.MediaId}
                photo={photo}
                onDelete={() => setPhotoToDelete(photo.MediaId)}
                onClick={() => setViewerIndex(index)}
              />
            ))}
          </SortableContext>

          {/* LOCAL PENDING ITEMS */}
          {pendingUploads.map((photo, index) => {
            const globalOffset = photoArray.length;
            return (
              <div
                key={photo.uniqueKey}
                className={styles.photoCard}
                style={{ position: "relative" }}
              >
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => setPhotoToDelete(photo.uniqueKey!)}
                >
                  <MdDelete size={18} />
                </button>
                <div
                  style={{ position: "absolute", inset: 0, zIndex: 1 }}
                  onClick={() => setViewerIndex(globalOffset + index)}
                >
                  <MediaStateOverlay
                    status={photo.Status as any}
                    theme="light"
                  />
                </div>
                <img
                  src={photo.localUrl}
                  alt="Processing"
                  className={styles.photoImage}
                />
              </div>
            );
          })}
        </div>
      </DndContext>

      <DeleteConfirmationModal
        isOpen={!!photoToDelete}
        onClose={() => setPhotoToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete Photo?"
        description="Are you sure you want to remove this photo? This action cannot be undone."
        confirmText="Delete Photo"
      />
      <DeleteConfirmationModal
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={confirmDeleteAll}
        title="Delete All Photos?"
        description={`This will permanently remove all ${photoArray.length + pendingUploads.length} photos from your listing. Are you sure?`}
        confirmText="Delete All Permanently"
      />
      <PhotoViewerDialog
        isOpen={viewerIndex !== null}
        onClose={() => setViewerIndex(null)}
        photos={allViewablePhotos}
        currentIndex={viewerIndex || 0}
        onNavigate={setViewerIndex}
      />
      <CategoryOrganizerModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        photos={photoArray}
      />
    </div>
  );
}
